import * as THREE from "three";
import { RigidRig } from "../../rigidRig";

/**
 * Hiding stock parts (kit/hide.ts) costs nothing on the GPU's side: a hidden vertex is sent outside the clip volume, so
 * the hidden part's triangles collapse to nothing before rasterisation (no fragments, no overdraw). The body material
 * (kit/bodyMaterial.ts) and the shadow depth material here share the snippet, so a hidden roof casts no shadow either
 * (the towers' shadow proxies, views/towerLod.ts, are reduced copies that keep `tdHide`).
 */
export const KIT_HIDE_GLSL = {
  /** Declarations (after `#include <common>`). */
  head: "attribute float tdHide;\nuniform float uKHide;",
  /** After `#include <project_vertex>`: a vertex of a hidden group goes to a point past the far plane. */
  body: `
  {
    int tdHb = int(tdHide + 0.5);
    if (tdHb > 0 && ((int(uKHide + 0.5) >> tdHb) & 1) == 1) gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
  }`,
} as const;

/** The shadow pass's depth materials of the kits that hide something: one per hide mask, one program for all. */
export class KitHideDepth {
  private static readonly byMask = new Map<number, THREE.MeshDepthMaterial>();

  static material(mask: number): THREE.MeshDepthMaterial {
    let m = KitHideDepth.byMask.get(mask);
    if (m) return m;
    m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    m.name = `kit-hide-depth-${mask}`;
    const u = { uKHide: { value: mask } };
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, u);
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", `#include <common>\n${KIT_HIDE_GLSL.head}`)
        .replace("#include <project_vertex>", `#include <project_vertex>${KIT_HIDE_GLSL.body}`);
    };
    m.customProgramCacheKey = () => "kit-hide-depth";
    KitHideDepth.byMask.set(mask, m);
    return m;
  }

  /**
   * Puts the hide mask's depth material on every rigid-rig caster under `root` that carries `tdHide` (and its shadow
   * proxies), or back to the rigid rig's shared one for mask 0.
   */
  static dress(root: THREE.Object3D, mask: number): void {
    const depth = mask ? KitHideDepth.material(mask) : RigidRig.depth;
    root.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh || o.userData.tdKit === true || !m.geometry.getAttribute("tdHide")) return;
      if (m.customDepthMaterial === RigidRig.depth || m.customDepthMaterial?.name.startsWith("kit-hide-depth")) m.customDepthMaterial = depth;
    });
  }
}
